// src/hooks/useReactQueryData.js

import { useQueryClient, useQuery } from '@tanstack/react-query'
import { fetchEssentialData, fetchBackgroundData, warmCache, fetchEntity } from '../lib/api-queries'
import { queryKeys, getEntityQueryKey, CACHE_TIMES } from '../lib/query-client'
import { LOADING_PHASES, ROLE_DATA_PATTERNS } from '../lib/EntityMapping'
import { logger } from '../lib/logger'

// bootLoadingFlags: array of `isLoading` booleans from per-entity boot-set
// queries (e.g. clients). Each new boot-set entity batch pushes its query's
// isLoading onto this array at the call site. OR-folded into bootSetLoading
// below so the splash gate keeps blocking until every boot-set query hydrates.
export const useReactQueryData = (companyId, userContext = {}, authReady = false, bootLoadingFlags = []) => {
  
  const queryClient = useQueryClient()
  const { role = 'owner', timeOfDay, season } = userContext

  // Essential data query (replaces ProgressiveLoader essential phase)
  const essentialQuery = useQuery({
    queryKey: queryKeys.allCompanyData(companyId),
    queryFn: () => fetchEssentialData(companyId),
    enabled: !!companyId && authReady,
    staleTime: CACHE_TIMES.ESSENTIAL_STALE,
    gcTime: CACHE_TIMES.GC_TIME,
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  })

  // Background data query (replaces ProgressiveLoader background phase)
  // PERFORMANCE FIX: Re-enabled with proper safeguards to prevent infinite loops
  //
  // Background data (services, expense categories, etc.) changes rarely.
  // Disabled refetch triggers to minimize API calls — data refreshes
  // only when staleTime (10 min) expires and a new query is made.
  const backgroundQuery = useQuery({
    queryKey: [...queryKeys.allCompanyData(companyId), 'background'],
    queryFn: () => fetchBackgroundData(companyId),
    // Fixed infinite loop by removing dependency on essentialQuery.isSuccess
    // Only enable when auth is ready and companyId exists
    enabled: !!companyId && authReady,
    staleTime: CACHE_TIMES.BACKGROUND_STALE,
    gcTime: 15 * 60 * 1000, // 15 minutes
    retry: 2,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchOnMount: false, // Prevent refetch on component mount
    refetchOnWindowFocus: false, // Prevent refetch when window gains focus
    refetchOnReconnect: false, // Prevent refetch on network reconnect
  })

  // Role-based data query (replaces BackgroundRefreshManager)
  // PERFORMANCE FIX: Re-enabled with proper safeguards to prevent infinite loops
  const roleQuery = useQuery({
    queryKey: queryKeys.roleData(role, companyId),
    queryFn: async () => {
      const roleEntities = ROLE_DATA_PATTERNS[role] || []
      const essentialEntities = LOADING_PHASES.essential
      const uniqueRoleEntities = roleEntities.filter(entity => !essentialEntities.includes(entity))

      if (uniqueRoleEntities.length === 0) {
        return {} // No unique role entities, return empty object
      }

      // Only fetch unique role entities
      const results = {}
      for (const entityName of uniqueRoleEntities) {
        try {
          results[entityName] = await fetchEntity(entityName, companyId)
        } catch (error) {
          console.warn(`[API Queries] Failed to load role entity ${entityName}:`, error)
          results[entityName] = []
        }
      }
      return results
    },
    // Fixed infinite loop by removing dependency on essentialQuery.isSuccess
    // Only enable when auth is ready and companyId exists
    enabled: !!companyId && authReady && !!role,
    staleTime: CACHE_TIMES.ESSENTIAL_STALE, // 5 minutes for role-specific data
    gcTime: CACHE_TIMES.GC_TIME,
    refetchInterval: false, // Disable automatic refetching
    refetchOnMount: false, // Prevent refetch on component mount
    refetchOnWindowFocus: false, // Prevent refetch when window gains focus
    refetchOnReconnect: false, // Prevent refetch on network reconnect
    retry: 2,
  })

  // Individual entity queries removed to prevent duplication with essential data query
  // Essential data query already covers all essential entities

  // Combine all loaded data
  const loadedData = {
    ...essentialQuery.data,
    ...backgroundQuery.data,
    ...roleQuery.data,
  }

  // Debug logging for critical entities
  if (process.env.NODE_ENV === 'development' && Math.random() < 0.01) {
    logger.reactQuery('Essential query data:', essentialQuery.data)
    logger.reactQuery('Jobs in essential data:', essentialQuery.data?.jobs?.length || 0)
    logger.reactQuery('Employees in essential data:', essentialQuery.data?.employees?.length || 0)
    logger.reactQuery('Combined loadedData keys:', Object.keys(loadedData))
    logger.reactQuery('Jobs in loadedData:', loadedData?.jobs?.length || 0)
    logger.reactQuery('Employees in loadedData:', loadedData?.employees?.length || 0)
  }

  // Debug logging for data updates
  if (process.env.NODE_ENV === 'development') {
    // Removed excessive logging
  }

  // Boot set: entities whose load blocks the splash gate. Starts with the
  // essential mega-query and folds in any per-entity boot-set queries the
  // caller passes via bootLoadingFlags (clients is the first; jobs/employees
  // follow in their own batches).
  const bootSetLoading =
    essentialQuery.isLoading || bootLoadingFlags.some(Boolean)

  // Loading state that matches your current API
  const loadingState = {
    isInitializing: bootSetLoading,
    isBackgroundRefreshing: essentialQuery.isFetching && !essentialQuery.isLoading,
    isCacheWarming: false, // Will be handled by warmCache function
    loadingProgress: calculateLoadingProgress(),
    lastRefresh: new Date().toISOString(),
    errors: [
      ...(essentialQuery.error ? [essentialQuery.error.message] : []),
      ...(backgroundQuery.error ? [backgroundQuery.error.message] : []),
      ...(roleQuery.error ? [roleQuery.error.message] : []),
    ],
    loadingTimeout: false,
  }

  // Performance metrics (simplified - React Query DevTools provides better insights)
  const performanceMetrics = {
    essentialDataLoadTime: essentialQuery.dataUpdatedAt - essentialQuery.dataUpdatedAt,
    backgroundDataLoadTime: backgroundQuery.dataUpdatedAt - backgroundQuery.dataUpdatedAt,
    totalQueries: 3, // essential, background, role queries
    successfulQueries: [essentialQuery, backgroundQuery, roleQuery].filter(q => q.isSuccess).length,
    failedQueries: [essentialQuery, backgroundQuery, roleQuery].filter(q => q.isError).length,
  }

  // Calculate loading progress
  function calculateLoadingProgress() {
    const queries = [essentialQuery, backgroundQuery, roleQuery]
    const completedQueries = queries.filter(q => q.isSuccess).length
    return queries.length > 0 ? Math.round((completedQueries / queries.length) * 100) : 0
  }

  // Refresh function (replaces your refreshData)
  const refreshData = async (dataType = null) => {
    if (!companyId || !authReady) {
      return
    }

    try {
      // Handle array of data types (optimized for multiple refreshes)
      if (Array.isArray(dataType)) {
        console.log(`[useReactQueryData] Refreshing multiple data types: ${dataType.join(', ')}`)
        
        // Invalidate and refetch the allCompanyData query
        await queryClient.invalidateQueries({
          queryKey: queryKeys.allCompanyData(companyId)
        })
        
        await queryClient.refetchQueries({
          queryKey: queryKeys.allCompanyData(companyId)
        })
        
        return
      }
      
      if (dataType) {
        // If a specific data type is requested, only refresh that specific query
        // Removed excessive logging - only log in development if needed
        if (process.env.NODE_ENV === 'development' && Math.random() < 0.1) {
            console.log(`[useReactQueryData] Refreshing specific data type: ${dataType}`)
        }
        
        // Invalidate the per-entity query key. queryKeys.entity expects
        // (entityName, companyId) — earlier this site passed them swapped,
        // generating a phantom key nothing read from, so every refreshData
        // call against a migrated entity was a silent no-op. Fixed here.
        await queryClient.invalidateQueries({
          queryKey: queryKeys.entity(dataType, companyId)
        })

        // Also invalidate the legacy mega-query (still holds non-migrated
        // entities); harmless if the entity isn't in it anymore.
        await queryClient.invalidateQueries({
          queryKey: queryKeys.allCompanyData(companyId)
        })

        // Refetch the per-entity key.
        await queryClient.refetchQueries({
          queryKey: queryKeys.entity(dataType, companyId)
        })
      } else {
        // If no specific data type, refresh all data (legacy behavior)
        console.log('[useReactQueryData] Refreshing all company data')
        
        // Invalidate and refetch the essential data query
        await queryClient.invalidateQueries({
          queryKey: queryKeys.allCompanyData(companyId)
        })
        
        // Also invalidate the background query if it exists
        await queryClient.invalidateQueries({
          queryKey: [...queryKeys.allCompanyData(companyId), 'background']
        })
        
        // Force refetch the essential query to ensure data is updated immediately
        await queryClient.refetchQueries({
          queryKey: queryKeys.allCompanyData(companyId)
        })
      }
      
    } catch (error) {
      console.error('[useReactQueryData] Refresh failed:', error)
      throw error
    }
  }

  // Cache warming function (replaces your warmCache)
  const warmCacheData = async () => {
    if (!companyId || !authReady) {
      return
    }

    // Add debugging to track when warmCache is called
    if (process.env.NODE_ENV === 'development') {
      // Removed excessive logging
    }

    try {
      // Warm cache with user context - this handles all the data fetching
      await warmCache(companyId, userContext)
    } catch (error) {
      console.error('[useReactQueryData] Cache warming failed:', error)
      throw error
    }
  }

  // Get specific data with loading state (replaces your getData)
  const getData = (dataType) => {
    // Since we removed individual entity queries, all data comes from the combined queries
    const result = {
      data: loadedData[dataType] || [],
      isLoading: loadingState.isInitializing,
      isLoaded: !!loadedData[dataType],
      error: null,
    }
    
    // Debug logging for critical entities (reduced frequency)
    if (['jobs', 'employees'].includes(dataType) && process.env.NODE_ENV === 'development' && Math.random() < 0.05) {
      logger.reactQuery(`getData(${dataType}):`, {
        dataLength: result.data?.length || 0,
        isLoading: result.isLoading,
        isLoaded: result.isLoaded,
        hasDataInLoadedData: !!loadedData[dataType],
        loadedDataKeys: Object.keys(loadedData)
      })
    }
    
    return result
  }

  // Get phase status (replaces your getPhaseStatus)
  const getPhaseStatus = (phase) => {
    if (phase === 'essential') {
      return {
        loaded: essentialQuery.isSuccess,
        loading: essentialQuery.isLoading,
        error: essentialQuery.error,
      }
    }
    
    if (phase === 'background') {
      return {
        loaded: backgroundQuery.isSuccess,
        loading: backgroundQuery.isLoading,
        error: backgroundQuery.error,
      }
    }

    return { loaded: false, loading: false, error: null }
  }

  // Performance report (simplified)
  const getPerformanceReport = () => {
    return {
      ...performanceMetrics,
      queryCache: queryClient.getQueryCache().getAll().length,
      memoryUsage: performance.memory?.usedJSHeapSize || 0,
      timestamp: new Date().toISOString(),
    }
  }

  // Check if data is loaded (replaces your isDataLoaded)
  const isDataLoaded = (dataType) => {
    return !!loadedData[dataType] && loadedData[dataType].length > 0
  }

  // Get loading progress (replaces your getLoadingProgress)
  const getLoadingProgress = () => {
    return calculateLoadingProgress()
  }

  return {
    // Data
    loadedData,
    
    // Loading states
    loadingState,
    
    // Performance
    performanceMetrics,
    
    // Actions
    refreshData,
    warmCache: warmCacheData,
    getData,
    getPhaseStatus,
    getPerformanceReport,
    
    // Utilities
    isDataLoaded,
    getLoadingProgress,
    
    // React Query specific
    queryClient,
    essentialQuery,
    backgroundQuery,
    roleQuery,
  }
}

// Individual entity hook for components that only need specific data
export const useEntityData = (entityName, companyId, authReady = false) => {
  return useQuery({
    queryKey: getEntityQueryKey(entityName, companyId),
    queryFn: () => fetchEntity(entityName, companyId),
    enabled: !!companyId && authReady,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 3,
  })
}


export default useReactQueryData